import React, { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, ArrowsCounterClockwise, FileArrowDown, Wallet, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

const STAGE_LABEL = { manager: "Manager review", finance: "Finance payment" };

const fmtInr = (n) => "₹" + (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

function Stepper({ workflow, submitter }) {
  const nodes = [
    { key: "submit", label: "Submitted", status: "approved", actor: submitter?.name, at: submitter?.at, comment: "" },
    ...(workflow || []).map((w) => ({ ...w, key: w.role, label: STAGE_LABEL[w.role] || w.role })),
  ];
  return (
    <ol className="relative border-l border-zinc-200 pl-6 ml-3 space-y-5">
      {nodes.map((n) => {
        const rejected = n.status === "rejected";
        const approved = n.status === "approved";
        const pending = n.status === "pending";
        const correction = n.status === "correction_requested";
        let dotClass = "bg-white border-zinc-300 text-zinc-400";
        if (approved) dotClass = "bg-emerald-50 border-emerald-200 text-emerald-600";
        else if (rejected) dotClass = "bg-rose-50 border-rose-200 text-rose-600";
        else if (pending) dotClass = "bg-amber-50 border-amber-200 text-amber-600";
        else if (correction) dotClass = "bg-orange-50 border-orange-200 text-orange-600";
        return (
          <li key={n.key} className="relative">
            <span className={`absolute -left-[34px] flex h-7 w-7 items-center justify-center rounded-full border ${dotClass}`}>
              {approved ? <CheckCircle size={16} weight="duotone" /> :
                rejected ? <XCircle size={16} weight="duotone" /> :
                correction ? <ArrowsCounterClockwise size={16} weight="duotone" /> :
                <span className="h-2 w-2 rounded-full bg-current" />}
            </span>
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-zinc-900">{n.label}</span>
                {(n.actor?.name || n.actor) && (
                  <span className="text-xs text-zinc-500">· {n.actor?.name || n.actor}</span>
                )}
              </div>
              {n.at && <span className="text-xs text-zinc-500 font-mono">{new Date(n.at).toLocaleString()}</span>}
              {n.comment && (
                <p className="mt-1 text-sm rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-zinc-700">“{n.comment}”</p>
              )}
              {pending && <span className="text-xs text-amber-700 font-medium mt-0.5">Awaiting</span>}
              {correction && <span className="text-xs text-orange-700 font-medium mt-0.5">Correction requested</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ReceiptView({ receipt, legacyUrl }) {
  if (!receipt && !legacyUrl) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">Bill</div>
      {receipt ? (
        <div data-testid="bill-attachment">
          {receipt.mime.startsWith("image/") ? (
            <img src={receipt.url} alt="Bill" data-testid="bill-image"
              onClick={() => window.open(receipt.url, "_blank")}
              className="max-h-72 w-auto rounded-md border border-zinc-200 cursor-zoom-in" />
          ) : (
            <div className="text-sm text-zinc-600">PDF attachment: <span className="font-mono">{receipt.name}</span></div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <button type="button" onClick={() => window.open(receipt.url, "_blank")}
              data-testid="bill-open-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-md text-xs hover:bg-zinc-50">
              <ArrowSquareOut size={14} /> Full size
            </button>
            <a href={receipt.url} download={receipt.name} data-testid="bill-download-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-md text-xs hover:bg-zinc-50">
              <FileArrowDown size={14} /> Download
            </a>
          </div>
        </div>
      ) : legacyUrl.startsWith("data:image") ? (
        <img src={legacyUrl} alt="Bill" data-testid="bill-image"
          className="max-h-72 rounded-md border border-zinc-200" />
      ) : (
        <a href={legacyUrl} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-md text-sm hover:bg-zinc-50">
          <FileArrowDown size={16} /> Open bill
        </a>
      )}
    </div>
  );
}

function PaidDetails({ exp }) {
  if (exp.status !== "paid" || !exp.utr) return null;
  const downloadInvoice = async () => {
    try {
      const { data } = await api.get(`/expenses/${exp.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exp.invoice_no || "invoice-" + exp.bill_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice");
    }
  };
  return (
    <div className="bg-green-50 border border-green-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-[0.16em] text-green-700 mb-2 flex items-center gap-1.5">
        <Wallet size={14} weight="duotone" /> Payment details
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><div className="text-xs text-green-800/70">UTR</div><div className="font-mono text-green-900">{exp.utr}</div></div>
        <div><div className="text-xs text-green-800/70">Payment date</div><div className="font-mono text-green-900">{exp.payment_date}</div></div>
        {exp.invoice_no && (
          <div><div className="text-xs text-green-800/70">Invoice No</div><div className="font-mono text-green-900" data-testid="invoice-no">{exp.invoice_no}</div></div>
        )}
        {exp.payment_remark && <div className="col-span-2"><div className="text-xs text-green-800/70">Note</div><div className="text-green-900">{exp.payment_remark}</div></div>}
      </div>
      <button
        type="button"
        onClick={downloadInvoice}
        data-testid="download-invoice-btn"
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-green-300 bg-white text-green-700 text-xs font-medium hover:bg-green-100 transition-colors duration-200"
      >
        <FileArrowDown size={14} /> Download payment invoice
      </button>
    </div>
  );
}

function PayeeAccountCard({ account, amount, billNumber, submitterName }) {
  if (!account || (!account.upi_id && !account.account_number)) return null;
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 mb-4 flex flex-wrap gap-4" data-testid="payee-account-card">
      <div className="flex-1 min-w-[180px] text-sm space-y-1.5">
        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Payee account (submitter)</div>
        {account.account_holder && (
          <div><span className="text-xs text-zinc-500">Holder:</span> <span className="text-zinc-900">{account.account_holder}</span></div>
        )}
        {account.bank_name && (
          <div><span className="text-xs text-zinc-500">Bank:</span> <span className="text-zinc-900">{account.bank_name}</span></div>
        )}
        {account.account_number && (
          <div><span className="text-xs text-zinc-500">A/C:</span> <span className="font-mono text-zinc-900">{account.account_number}</span></div>
        )}
        {account.ifsc && (
          <div><span className="text-xs text-zinc-500">IFSC:</span> <span className="font-mono text-zinc-900">{account.ifsc}</span></div>
        )}
        {account.upi_id && (
          <div><span className="text-xs text-zinc-500">UPI:</span> <span className="font-mono text-zinc-900">{account.upi_id}</span></div>
        )}
      </div>
      {account.upi_id && (
        <div className="text-center">
          <div className="inline-block p-2 bg-white border border-zinc-200 rounded-md">
            <QRCodeSVG
              data-testid="pay-upi-qr"
              size={130}
              value={`upi://pay?pa=${encodeURIComponent(account.upi_id)}&pn=${encodeURIComponent(account.account_holder || submitterName)}&am=${Number(amount).toFixed(2)}&cu=INR&tn=${encodeURIComponent("Bill " + billNumber)}`}
            />
          </div>
          <div className="text-[10px] text-zinc-500 mt-1.5">Scan to pay {fmtInr(amount)}</div>
        </div>
      )}
    </div>
  );
}

function ManagerActions({ comment, setComment, busy, onAct }) {
  return (
    <div className="border-t border-zinc-200 pt-4">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">Manager decision</div>
      <Textarea data-testid="approval-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment (optional)" rows={3} />
      <div className="flex items-center flex-wrap gap-2 mt-3">
        <Button onClick={() => onAct("approve")} disabled={busy} data-testid="approve-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <CheckCircle size={16} className="mr-1.5" /> Approve
        </Button>
        <Button onClick={() => onAct("reject")} disabled={busy} data-testid="reject-btn"
          variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50">
          <XCircle size={16} className="mr-1.5" /> Reject
        </Button>
        <Button onClick={() => onAct("request_correction")} disabled={busy} data-testid="correction-btn"
          variant="outline" className="border-orange-200 text-orange-700 hover:bg-orange-50">
          <ArrowsCounterClockwise size={16} className="mr-1.5" /> Request correction
        </Button>
      </div>
    </div>
  );
}

function PaymentForm({ pay, setPay, busy, onSubmit }) {
  return (
    <>
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-3">Record payment</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="utr">UTR number</Label>
          <Input id="utr" data-testid="pay-utr" value={pay.utr} onChange={(e) => setPay({ ...pay, utr: e.target.value })} className="mt-1 font-mono" />
        </div>
        <div>
          <Label htmlFor="pdate">Payment date</Label>
          <Input id="pdate" data-testid="pay-date" type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} className="mt-1 font-mono" />
        </div>
      </div>
      <div className="mt-3">
        <Label htmlFor="premark">Note (optional)</Label>
        <Textarea id="premark" data-testid="pay-remark" rows={2} value={pay.payment_remark} onChange={(e) => setPay({ ...pay, payment_remark: e.target.value })} className="mt-1" />
      </div>
      <Button onClick={onSubmit} disabled={busy} data-testid="mark-paid-btn"
        className="mt-3 bg-blue-600 hover:bg-blue-700 text-white">
        <Wallet size={16} className="mr-1.5" /> Mark as paid
      </Button>
    </>
  );
}

export default function ExpenseDetail({ expenseId, onClose, onChanged, role }) {
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [pay, setPay] = useState({ utr: "", payment_date: new Date().toISOString().slice(0, 10), payment_remark: "" });
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null); // {url, mime, name}

  useEffect(() => {
    if (!expenseId) { setExp(null); return; }
    setLoading(true); setComment(""); setPay({ utr: "", payment_date: new Date().toISOString().slice(0, 10), payment_remark: "" });
    api.get(`/expenses/${expenseId}`).then(({ data }) => setExp(data)).finally(() => setLoading(false));
  }, [expenseId]);

  // Load receipt image/PDF as an authenticated blob so it renders reliably
  useEffect(() => {
    let objUrl = null;
    setReceipt(null);
    if (exp?.receipt_file_id) {
      api.get(`/files/${exp.receipt_file_id}`, { responseType: "blob" })
        .then(({ data }) => {
          objUrl = URL.createObjectURL(data);
          setReceipt({ url: objUrl, mime: data.type || "", name: exp.receipt_name || "bill" });
        })
        .catch(() => setReceipt(null));
    }
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [exp?.receipt_file_id, exp?.receipt_name]);

  const canManagerAct = exp && role === "manager" && exp.status === "pending" && exp.current_stage === "manager";
  const canFinancePay = exp && (role === "finance" || role === "admin") && exp.status === "approved";

  const actManager = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/expenses/${exp.id}/action`, { action, comment });
      setExp(data);
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Correction requested");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitPayment = async () => {
    if (!pay.utr.trim()) { toast.error("UTR is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/expenses/${exp.id}/payment`, pay);
      setExp(data);
      toast.success("Marked as paid");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const deleteExpense = async () => {
    if (!window.confirm("Delete this bill permanently? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/expenses/${exp.id}`);
      toast.success("Bill deleted");
      onChanged?.();
      onClose?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={!!expenseId} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] overflow-y-auto">
        {!exp && (
          <SheetHeader className="sr-only">
            <SheetTitle>Expense detail</SheetTitle>
            <SheetDescription>Expense bill details</SheetDescription>
          </SheetHeader>
        )}
        {loading && <div className="p-6 text-sm text-zinc-500">Loading…</div>}
        {exp && (
          <>
            <SheetHeader>
              <SheetDescription className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
                {exp.id.slice(0, 8)} · Bill #{exp.bill_number}
              </SheetDescription>
              <SheetTitle className="font-heading text-2xl">{fmtInr(exp.amount)}</SheetTitle>
              <div className="flex items-center gap-2 pt-1">
                <StatusBadge status={exp.status === "correction_requested" ? "pending" : exp.status} testId="detail-status" />
                <span className="text-xs text-zinc-500 font-mono">{exp.category}</span>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Expense date</div>
                  <div className="text-zinc-900 font-mono">{exp.expense_date}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Hub</div>
                  <div className="text-zinc-900">{exp.hub || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Vendor / Owner</div>
                  <div className="text-zinc-900">{exp.vendor_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Submitted by</div>
                  <div className="text-zinc-900">{exp.submitter_name}</div>
                </div>
                {exp.manager_approver_name && (
                  <div className="col-span-2" data-testid="approved-by-info">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Approved by (Manager)</div>
                    <div className="text-zinc-900">
                      {exp.manager_approver_name}
                      {exp.approved_at && (
                        <span className="text-xs text-zinc-500 font-mono ml-2">
                          {new Date(exp.approved_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {exp.remarks && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Remarks</div>
                  <p className="text-sm text-zinc-800">{exp.remarks}</p>
                </div>
              )}

              <ReceiptView receipt={receipt} legacyUrl={exp.receipt_data_url} />

              <PaidDetails exp={exp} />

              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-3">Workflow</div>
                <Stepper workflow={exp.workflow} submitter={{ name: exp.submitter_name, at: exp.created_at }} />
              </div>

              {canManagerAct && (
                <ManagerActions comment={comment} setComment={setComment} busy={busy} onAct={actManager} />
              )}

              {canFinancePay && (
                <div className="border-t border-zinc-200 pt-4">
                  <PayeeAccountCard
                    account={exp.submitter_account}
                    amount={exp.amount}
                    billNumber={exp.bill_number}
                    submitterName={exp.submitter_name}
                  />
                  <PaymentForm pay={pay} setPay={setPay} busy={busy} onSubmit={submitPayment} />
                </div>
              )}

              {role === "admin" && (
                <div className="border-t border-zinc-200 pt-4">
                  <Button onClick={deleteExpense} disabled={busy} data-testid="delete-expense-btn"
                    variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50">
                    <Trash size={16} className="mr-1.5" /> Delete bill
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
